import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FitText, Text } from '@repletoryreach/sdk-ui';
import './stat-card.css';

export interface StatCardProps {
  /** What the number counts. Short, muted, above the value. */
  label: ReactNode;
  /** The figure itself. Pass a formatted string, not a raw number. */
  value: ReactNode;
  /** One muted line of context under the value. Shown alongside `delta`, not instead of it. */
  hint?: ReactNode;
  /** Period-on-period movement. Positive is not always good, so `good` decides the colour. */
  delta?: { value: string; direction: 'up' | 'down'; good?: boolean; caption?: ReactNode };
  /** A small tinted tile. Pass any 18px icon; the tint comes from `tone`. */
  icon?: ReactNode;
  /** Colours the icon tile, the value and the bottom rule. */
  tone?: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
  /** Draws a 2px rule along the bottom edge, for a row of cards that is a breakdown. */
  withRule?: boolean;
  /** Where the card leads. Rendered as a footer link. */
  to?: string;
  linkLabel?: ReactNode;
  /** Shown in place of the value while the query is in flight. */
  loading?: boolean;
  /** A tiny series behind the value. Values are scaled to fit; fewer than two points draws nothing. */
  sparkline?: number[];
}

const TONE_VAR: Record<NonNullable<StatCardProps['tone']>, string> = {
  primary: 'var(--rr-color-primary)',
  success: 'var(--rr-color-success)',
  warning: 'var(--rr-color-warning)',
  error: 'var(--rr-color-error)',
  info: 'var(--rr-color-info)',
  neutral: 'var(--rr-color-text-muted)'
};

/**
 * The card the eye lands on first: one figure, what it counts, and how it is
 * moving. Built once here because the console shows a row of these above nearly
 * every page, and seven of them drawn seven ways is what makes a console feel
 * assembled rather than designed.
 *
 * A number the page could not load is not a zero. Pass `loading` while the
 * query is in flight and render the card only once you can say something true.
 */
export function StatCard({
  label, value, hint, delta, icon, tone = 'primary', withRule = false, to, linkLabel, loading = false, sparkline
}: StatCardProps) {
  const accent = TONE_VAR[tone];
  const deltaGood = delta ? (delta.good ?? delta.direction === 'up') : false;

  const body = (
    <>
      <div className="rr-stat-card__head">
        {icon && (
          <span className="rr-stat-card__icon" style={{ color: accent, background: `color-mix(in srgb, ${accent} 12%, transparent)` }} aria-hidden>
            {icon}
          </span>
        )}
        <Text role="overline" c="var(--rr-color-text-muted)" className="rr-stat-card__label">{label}</Text>
      </div>

      <div className="rr-stat-card__value">
        {loading
          ? <span className="rr-stat-card__skeleton" aria-hidden />
          // Money and dates are long; the figure shrinks to the card instead of
          // wrapping, so a KPI is always one line and always whole.
          : <Text role="display" fw={700} style={{ minWidth: 0, flex: '1 1 auto' }}><FitText>{value}</FitText></Text>}
      </div>

      {delta && !loading && (
        <div className="rr-stat-card__delta">
          <span style={{ color: deltaGood ? 'var(--rr-color-success)' : 'var(--rr-color-error)' }}>
            {delta.direction === 'up' ? '↑' : '↓'} {delta.value}
          </span>
          {delta.caption && <Text role="caption" c="var(--rr-color-text-muted)">{delta.caption}</Text>}
        </div>
      )}

      {/* A card can carry both: the delta says how it is moving, the hint says
          what the figure counts. They answer different questions, so showing
          one only when the other is absent was a false choice. */}
      {hint && <Text role="caption" c="var(--rr-color-text-muted)" className="rr-stat-card__hint">{hint}</Text>}

      {sparkline && sparkline.length > 1 && <Sparkline points={sparkline} colour={accent} />}
    </>
  );

  return (
    <div className={`rr-stat-card${withRule ? ' rr-stat-card--ruled' : ''}`} style={withRule ? { ['--rr-stat-accent' as string]: accent } : undefined}>
      {body}
      {to && (
        <Link to={to} className="rr-stat-card__link">
          {linkLabel} <span aria-hidden>→</span>
        </Link>
      )}
    </div>
  );
}

/** A flat area behind the figure. Decorative: the axis values are in the card's text. */
function Sparkline({ points, colour }: { points: number[]; colour: string }) {
  const w = 100;
  const h = 28;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  const step = w / (points.length - 1);
  const line = points.map((p, i) => `${i * step},${h - ((p - min) / span) * h}`).join(' ');
  return (
    <svg className="rr-stat-card__spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden focusable="false">
      <polygon points={`0,${h} ${line} ${w},${h}`} fill={colour} opacity="0.12" />
      <polyline points={line} fill="none" stroke={colour} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
